import { requestIdleCallback } from '@theme/maths'

/**
 * @typedef {Record<string, Element | Element[] | undefined>} Refs
 */

/**
 * @template {Refs} T
 * @typedef {T & Refs} RefsType
 */

/**
 * @typedef {string | number | boolean} ParsedValue
 */

/**
 * @typedef {ParsedValue | Record<string, ParsedValue>} Data
 */

class MissingRefError extends Error {
  /**
   * @param {string} ref
   * @param {Component} component
   */
  constructor(ref, component) {
    super(
      `Required ref "${ref}" not found in component ${component.tagName.toLowerCase()}`
    )
  }
}

/**
 * @template {Refs} [T=Refs]
 */
export class Component extends HTMLElement {
  /** @type {RefsType<T>} */
  refs = {}

  /** @type {string[] | undefined} */
  requiredRefs

  /** @type {MutationObserver} */
  #mutationObserver = new MutationObserver((mutations) => {
    if (
      mutations.some(
        (mutation) =>
          (mutation.type === 'attributes' &&
            this.#isDescendant(mutation.target)) ||
          (mutation.type === 'childList' &&
            [...mutation.addedNodes, ...mutation.removedNodes].some(
              this.#isDescendant
            ))
      )
    ) {
      this.#updateRefs()
    }
  })

  /**
   * @param {Node} node
   * @returns {boolean}
   */
  #isDescendant = (node) => getClosestComponent(getAncestor(node)) === this
  connectedCallback() {
    registerEventListeners()
    this.#updateRefs()
    requestIdleCallback(() => {
      this.#mutationObserver.observe(this, {
        attributeFilter: ['ref'],
        attributes: true,
        childList: true,
        subtree: true
      })
    })
  }

  updatedCallback() {
    this.#mutationObserver.takeRecords()
    this.#updateRefs()
  }

  disconnectedCallback() {
    this.#mutationObserver.disconnect()
  }

  #updateRefs() {
    const refs = {}
    const elements = new Set()
    for (const element of this.querySelectorAll('[ref]')) {
      if (this.#isDescendant(element)) {
        elements.add(element)
      }
    }

    for (const ref of elements) {
      const refName = ref.getAttribute('ref') ?? ''
      const isArray = refName.endsWith('[]')
      const path = isArray ? refName.slice(0, -2) : refName

      if (isArray) {
        const array = Array.isArray(refs[path]) ? refs[path] : []
        array.push(ref)
        refs[path] = array
      } else {
        refs[path] = ref
      }
    }

    if (this.requiredRefs?.length) {
      for (const ref of this.requiredRefs) {
        if (!(ref in refs)) {
          throw new MissingRefError(ref, this)
        }
      }
    }

    this.refs = refs
  }
}

/**
 * @param {Node} node
 * @returns {Node | null}
 */
function getAncestor(node) {
  return node.parentNode ?? null
}

/**
 * @param {Node | null} node
 * @returns {Node | null}
 */
function getClosestComponent(node) {
  if (!node) {
    return null
  }
  if (node instanceof Component) {
    return node
  }
  if (
    node instanceof HTMLElement &&
    node.tagName.toLowerCase().endsWith('-component')
  ) {
    return node
  }
  const ancestor = getAncestor(node)
  if (ancestor) {
    return getClosestComponent(ancestor)
  }
  return null
}

let initialized = false

function registerEventListeners() {
  if (initialized) {
    return
  }
  initialized = true

  const events = ['click', 'change', 'focus', 'blur', 'submit']
  const shouldBubble = ['focus', 'blur']
  const expensiveEvents = ['pointerenter']

  for (const eventName of [...events, ...expensiveEvents]) {
    const attribute = `on:${eventName}`

    document.addEventListener(
      eventName,
      (event) => {
        const element = getElement(event)
        if (!element) {
          return
        }

        const proxiedEvent =
          event.target === element
            ? event
            : new Proxy(event, {
                get(target, property) {
                  if (property === 'target') {
                    return element
                  }
                  const value = Reflect.get(target, property)
                  if (typeof value === 'function') {
                    return value.bind(target)
                  }
                  return value
                }
              })

        const value = element.getAttribute(attribute) ?? ''
        const [selector, rawMethod] = value.split('/')
        const matches = value.match(/([/?][^/?]+)([/?][^/?]+)$/)
        const data = matches ? matches[2] : null
        let instance
        if (selector) {
          instance = selector.startsWith('#')
            ? document.querySelector(selector)
            : element.closest(selector)
        } else {
          instance = getClosestComponent(element)
        }

        if (!(instance instanceof Component && rawMethod)) {
          return
        }

        const method = rawMethod.replace(/\?.*/, '')
        const callback = instance[method]

        if (typeof callback === 'function') {
          const args = [proxiedEvent]
          if (data) {
            args.unshift(parseData(data))
          }
          callback.call(instance, ...args)
        }
      },
      { capture: true }
    )
  }

  /**
   * @param {Event} event
   * @returns {Element | null}
   */
  function getElement(event) {
    const target = event.composedPath?.()[0] ?? event.target
    if (!(target instanceof Element)) {
      return null
    }
    if (target.hasAttribute(`on:${event.type}`)) {
      return target
    }
    if (expensiveEvents.includes(event.type)) {
      return null
    }
    return event.bubbles || shouldBubble.includes(event.type)
      ? target.closest(`[on\\:${event.type}]`)
      : null
  }
}

/**
 * @param {string} str
 * @returns {Data}
 */
function parseData(str) {
  const delimiter = str[0]
  const data = str.slice(1)
  return delimiter === '?'
    ? Object.fromEntries(
        Array.from(new URLSearchParams(data).entries()).map(([key, value]) => [
          key,
          parseValue(value)
        ])
      )
    : parseValue(data)
}

/**
 * @param {string} str
 * @returns {ParsedValue}
 */
function parseValue(str) {
  if (str === 'true') {
    return true
  }
  if (str === 'false') {
    return false
  }
  const maybeNumber = Number(str)
  if (!Number.isNaN(maybeNumber) && str.trim() !== '') {
    return maybeNumber
  }
  return str
}
